import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, X, ChevronRight, Image as ImageIcon, PenLine } from 'lucide-react';

const S = {
  page: { padding: '14px 20px', fontFamily: "'DM Sans', sans-serif" },
  headerCard: { background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', padding: '20px 24px 18px', marginBottom: '18px' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--nd-tx25)', marginBottom: '10px' },
  breadcrumbActive: { color: 'rgba(34,211,165,0.55)' },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' },
  pageTitle: { fontSize: '26px', fontWeight: '800', color: 'var(--nd-tx85)', letterSpacing: '-0.03em', lineHeight: 1.1, margin: 0, display: 'flex', alignItems: 'center', gap: '12px' },
  pageSub: { fontSize: '12px', color: 'var(--nd-tx30)', marginTop: '5px' },
  countNum: { fontFamily: "'DM Mono',monospace", fontSize: '30px', fontWeight: '700', lineHeight: 1, background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  countLabel: { fontSize: '11px', color: 'var(--nd-tx25)', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px' },
  gradLine: { height: '2px', borderRadius: '2px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)', marginTop: '16px' },
  tableCard: { background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', overflow: 'hidden' },
  th: { padding: '12px 16px', fontSize: '10px', fontWeight: '700', color: 'var(--nd-tx25)', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'left', borderBottom: '1px solid var(--nd-w05)', background: 'var(--nd-w02)', whiteSpace: 'nowrap' },
  td: { padding: '13px 16px', fontSize: '13px', color: '#94A3B8', borderBottom: '1px solid var(--nd-w04)', whiteSpace: 'nowrap' },
  jobId: { fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  viewBtn: { padding: '5px 12px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", transition: 'opacity .2s' },
  spinner: { width: '36px', height: '36px', border: '3px solid var(--nd-w08)', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'acj-spin 1s linear infinite', margin: '0 auto' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.70)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' },
  modal: { background: 'var(--nd-surface)', border: '1px solid var(--nd-w08)', borderRadius: '16px', maxWidth: '700px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.60)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid var(--nd-w06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'var(--nd-surface)', zIndex: 2 },
  modalTitle: { fontSize: '15px', fontWeight: '800', color: 'var(--nd-text)', display: 'flex', alignItems: 'center', gap: '9px', margin: 0 },
  closeBtn: { width: '30px', height: '30px', borderRadius: '7px', background: 'var(--nd-w05)', border: '1px solid var(--nd-w08)', color: 'var(--nd-tx40)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBody: { padding: '20px 24px' },
  fieldRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--nd-w05)' },
  fieldLabel: { fontSize: '11px', fontWeight: '700', color: 'var(--nd-tx30)', letterSpacing: '0.10em', textTransform: 'uppercase', minWidth: '120px', paddingTop: '2px' },
  fieldValue: { fontSize: '13px', color: 'var(--nd-text)', fontWeight: '500' },
  imgGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px,1fr))', gap: '8px', marginTop: '12px' },
  imgThumb: { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--nd-w08)' },
  emptyImg: { padding: '24px', textAlign: 'center', color: 'var(--nd-tx22)', fontSize: '13px' },
  sig: { maxWidth: '180px', borderRadius: '8px', border: '1px solid var(--nd-w10)', padding: '10px', background: 'var(--nd-w03)', marginTop: '10px' },
};

export default function AdminCompletedJobs() {
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/completed-job-orders`)
      .then(res => setAllJobs(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const jobs = allJobs.filter(job => {
    if (!startDate && !endDate) return true;
    const jobDate = job.completed_at ? new Date(job.completed_at) : null;
    if (!jobDate) return false;
    
    jobDate.setHours(0,0,0,0);
    
    let isAfterStart = true;
    let isBeforeEnd = true;
    
    if (startDate) {
      const sDate = new Date(startDate);
      sDate.setHours(0,0,0,0);
      isAfterStart = jobDate >= sDate;
    }
    if (endDate) {
      const eDate = new Date(endDate);
      eDate.setHours(0,0,0,0);
      isBeforeEnd = jobDate <= eDate;
    }
    
    return isAfterStart && isBeforeEnd;
  });

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes acj-spin{to{transform:rotate(360deg)}} .acj-row:hover td{background:var(--nd-w02)!important} .acj-vbtn:hover{opacity:.82}`}</style>

      <div style={S.headerCard}>
        <div style={S.breadcrumb}><span>Admin</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={S.breadcrumbActive}>Completed Job Orders</span></div>
        <div style={S.titleRow}>
          <div>
            <div style={S.pageTitle}><ClipboardList size={22} color="#3B82F6"/>Completed Job Orders</div>
            <div style={S.pageSub}>All finished job orders across all teams</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={S.countNum}>{jobs.length}</div>
            <div style={S.countLabel}>Total Jobs</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--nd-tx30)', textTransform: 'uppercase', marginBottom: '4px' }}>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--nd-w08)', background: 'var(--nd-surface)', color: 'var(--nd-text)', fontSize: '13px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--nd-tx30)', textTransform: 'uppercase', marginBottom: '4px' }}>End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--nd-w08)', background: 'var(--nd-surface)', color: 'var(--nd-text)', fontSize: '13px' }} />
          </div>
          {(startDate || endDate) && (
            <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ marginTop: '18px', padding: '8px 14px', background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.2)', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>Clear</button>
          )}
        </div>
        <div style={S.gradLine}/>
      </div>

      <div style={S.tableCard}>
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={S.spinner}/><p style={{ color: 'var(--nd-tx30)', fontSize: '13px', marginTop: '14px' }}>Loading job orders…</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['#','Client','Phone','City','Address','System Size','Scheduled','Completed','Team','Action'].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {jobs.length > 0 ? jobs.map((job, i) => (
                  <tr key={job.job_order_id} className="acj-row">
                    <td style={{ ...S.td, color: 'var(--nd-tx40)' }}>{i+1}</td>
                    <td style={{ ...S.td, color: 'var(--nd-text)', fontWeight: '600' }}>{job.client_name}</td>
                    <td style={S.td}>{job.phone_number}</td>
                    <td style={S.td}>{job.client_city}</td>
                    <td style={{ ...S.td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.address}</td>
                    <td style={S.td}><span style={{ fontFamily: "'DM Mono',monospace", fontWeight: '700', color: '#22D3A5' }}>{job.system_size} kW</span></td>
                    <td style={S.td}>{job.scheduled_date ? new Date(job.scheduled_date).toLocaleDateString('en-GB') : '—'}</td>
                    <td style={S.td}>{job.completed_at ? new Date(job.completed_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td style={S.td}>{job.assigned_team}</td>
                    <td style={S.td}><button className="acj-vbtn" style={S.viewBtn} onClick={() => setSelectedJob(job)}>View</button></td>
                  </tr>
                )) : (
                  <tr><td colSpan="10" style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--nd-tx22)', fontSize: '13px' }}>No completed jobs found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedJob && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setSelectedJob(null)}>
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <h2 style={S.modalTitle}><ClipboardList size={17} color="#3B82F6"/>Job Order Details</h2>
              <button style={S.closeBtn} onClick={() => setSelectedJob(null)}><X size={15}/></button>
            </div>
            <div style={S.modalBody}>
              {[
                ['Client', selectedJob.client_name],
                ['Phone', selectedJob.phone_number],
                ['City', selectedJob.client_city],
                ['Address', selectedJob.address],
                ['Scheduled', selectedJob.scheduled_date],
                ['Completed', selectedJob.completed_at],
                ['Team', selectedJob.assigned_team],
                ['System Size', `${selectedJob.system_size} kW`],
              ].map(([label, val]) => (
                <div key={label} style={S.fieldRow}>
                  <div style={S.fieldLabel}>{label}</div>
                  <div style={S.fieldValue}>{val || '—'}</div>
                </div>
              ))}

              <div style={{ marginTop: '18px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--nd-tx30)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '7px' }}><PenLine size={13}/>Signature</div>
                {selectedJob.customer_signature
                  ? <img src={`data:image/png;base64,${selectedJob.customer_signature}`} alt="Signature" style={S.sig}/>
                  : <div style={S.emptyImg}>No signature available</div>}
              </div>

              <div style={{ marginTop: '18px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--nd-tx30)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '7px' }}><ImageIcon size={13}/>After Images</div>
                {(selectedJob.after_images || []).length > 0
                  ? <div style={S.imgGrid}>{(selectedJob.after_images).map((url, i) => <img key={i} src={url} alt="" style={S.imgThumb}/>)}</div>
                  : <div style={S.emptyImg}>No images available</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}